import React, {
    useState,
    useContext,
    useCallback,
    useMemo
} from 'react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import { TranslatorContext } from '@jutro/locale';
import { Button } from '@jutro/components';
import SagaCommonMessages from 'sg-common-messages';
import FormAccordion from 'sg-form-accordion';
import SpmUtils from 'sg-spm-utils';
import SgSpmAdditionalDriverForm from 'sg-spm-additional-driver-form';
import { CepConstants } from 'saga-cep-specific-components';
import { SgScrollUtils, useSgTrackPageName } from 'saga-modules';
import { events } from 'gw-platform-events-js';
import CommonConstants from 'sg-common-constants';
import SgDriverAdditionalQuestions from '../sg-driver-additional-questions/SgDriverAdditionalQuestions';
import AdditionalQuestionsAccordionMetadata from './AdditionalQuestionsAccordion.metadata.json5';
import metadata from './SgAddDriver.metadata.json5';
import messages from './SgAddDriver.messages';

function SgAddDriver(props) {
    const {
        id,
        modelVM,
        addingDriverIndex,
        updateData,
        handleCancel,
        handleGoBack,
        onContinue: onParentContinue
    } = props;

    const {
        onValidate,
        isComponentValid
    } = useValidation(id);

    const translator = useContext(TranslatorContext);

    const [showErrors, setShowErrors] = useState(false);
    const [showWizardContinue, setShowWizardContinue] = useState(false);

    const showDuplicatedPersonError = useCallback(() => {
        return SpmUtils.driverExists(modelVM, addingDriverIndex);
    }, [modelVM, addingDriverIndex]);

    useSgTrackPageName(CepConstants.PAGE_NAMES.DRIVER_CHANGES_ADD);

    const onContinue = useCallback(() => {
        if (!isComponentValid || showDuplicatedPersonError()) {
            setShowErrors(true);
            events.emit(CommonConstants.SHOW_VALIDATION_ERROR_EVENT);
            return; // Do not continue
        }
        onParentContinue();
    }, [isComponentValid, showDuplicatedPersonError, onParentContinue]);

    const goTopButtonContent = useMemo(() => {
        return (
            <div className="goTopButtonContainer">
                <Button
                    onClick={() => { SgScrollUtils.scrollToElementById('driverChangesSubHeader'); }}
                    id="goToTopButton"
                    className="goTopButton"
                >
                    <span className="goTopButtonText">
                        {translator(messages.GoTop)}
                    </span>
                </Button>
            </div>
        );
    }, [translator]);

    const commonProps = {
        modelVM,
        updateData,
        showErrors: showErrors,
        setShowErrors: setShowErrors,
    };

    const additionalAccordions = [
        {
            name: 'Additional questions - about vehicle',
            accordionComponents: {
                SgDriverAdditionalQuestions: {
                    name: 'SgDriverAdditionalQuestions',
                    controller: SgDriverAdditionalQuestions
                }
            },
            translations: {
                messages: messages.AdditionalQuestions,
                params: {
                    possessivePronoun: translator(SagaCommonMessages.theirPossessivePronoun),
                    pronoun: translator(SagaCommonMessages.theyPronoun)
                }
            },
            accordionMetadata: AdditionalQuestionsAccordionMetadata
        }
    ];

    const overrideProps = {
        additionalDriverForm: {
            commonProps,
            setShowWizardContinue: setShowWizardContinue,
            driverIndex: addingDriverIndex,
            onValidate,
            additionalAccordions,
            showDuplicatedPersonError,
            retirementDateQuestionAvailable: true,
            additionalDriverPageActions: {
                saveDriver: onContinue
            }
        },
        goTopButton: {
            content: goTopButtonContent
        },
        continueButton: {
            visible: showWizardContinue,
            onClick: onContinue
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate: onValidate,
            handleCancel,
            handleGoBack
        },
        resolveComponentMap: {
            SgSpmAdditionalDriverForm,
            FormAccordion
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={modelVM}
            overrideProps={overrideProps}
            onModelChange={updateData}
            onValidationChange={onValidate}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

export default SgAddDriver;
